import type { CSSProperties, ReactElement } from 'react';
import React from 'react';
import classNames from 'classnames';
import { TimelineHeadersConsumer } from './HeadersContext';
import SidebarHeader from './SidebarHeader';
import { RIGHT_VARIANT } from './constants';

export type TimelineHeadersProps = {
  registerScroll: (el: HTMLDivElement | null) => void;
  leftSidebarWidth: number;
  rightSidebarWidth?: number;
  style?: CSSProperties;
  children?: React.ReactNode | React.ReactNode[];
  className?: string;
  calendarHeaderStyle?: CSSProperties;
  calendarHeaderClassName?: string;
  headerRef?: (el: HTMLDivElement | null) => void;
  sticky?: boolean;
};

class TimelineHeaders extends React.Component<TimelineHeadersProps> {
  getRootStyle = (): CSSProperties => {
    return {
      ...this.props.style,
      display: 'flex',
      width: '300%',
      ...(this.props.sticky
        ? {
            position: 'sticky',
            zIndex: 101,
            top: 0,
          }
        : null),
    };
  };

  handleRootRef = (element: HTMLDivElement) => {
    if (this.props.headerRef) {
      this.props.headerRef(element);
    }
  };

  /**
   * check if child of type SidebarHeader
   * refer to for explanation https://github.com/gaearon/react-hot-loader#checking-element-types
   */
  isSidebarHeader = (child: React.ReactNode): child is ReactElement => {
    if (!child || typeof child !== 'object' || !('type' in child)) {
      return false;
    }
    const type = child.type;
    if (
      type === undefined ||
      typeof type === 'string' ||
      !('secretKey' in type)
    ) {
      return false;
    }
    return type.secretKey === SidebarHeader.secretKey;
  };

  render() {
    let rightSidebarHeader;
    let leftSidebarHeader;
    const {
      children,
      className,
      registerScroll,
      rightSidebarWidth,
      calendarHeaderStyle,
      calendarHeaderClassName,
    } = this.props;
    const calendarHeaders = React.Children.map(children, (child) => {
      if (this.isSidebarHeader(child)) {
        if (child.props.variant === RIGHT_VARIANT) {
          rightSidebarHeader = child;
        } else {
          leftSidebarHeader = child;
        }
      } else {
        return child;
      }
    });
    if (!leftSidebarHeader) {
      leftSidebarHeader = <SidebarHeader />;
    }
    if (!rightSidebarHeader && rightSidebarWidth) {
      rightSidebarHeader = <SidebarHeader variant="right" />;
    }
    return (
      <div
        ref={this.handleRootRef}
        data-testid="headerRootDiv"
        style={this.getRootStyle()}
        className={classNames('rct-header-root', className)}
      >
        {leftSidebarHeader}
        <div
          ref={registerScroll}
          style={{ ...calendarHeaderStyle, position: 'relative' }}
          className={classNames('rct-calendar-header', calendarHeaderClassName)}
          data-testid="headerContainer"
        >
          {calendarHeaders}
        </div>
        {rightSidebarHeader}
      </div>
    );
  }
}

export type TimelineHeadersWrapperProps = {
  sticky?: boolean;
  style?: CSSProperties;
  children: React.ReactNode;
  className?: string;
  calendarHeaderStyle?: CSSProperties;
  calendarHeaderClassName?: string;
};

const TimelineHeadersWrapper = ({
  children,
  style,
  className,
  calendarHeaderStyle,
  calendarHeaderClassName,
  sticky,
}: TimelineHeadersWrapperProps) => (
  <TimelineHeadersConsumer>
    {({ leftSidebarWidth, rightSidebarWidth, registerScroll }) => {
      return (
        <TimelineHeaders
          leftSidebarWidth={leftSidebarWidth}
          rightSidebarWidth={rightSidebarWidth}
          registerScroll={registerScroll}
          style={style}
          sticky={sticky}
          className={className}
          calendarHeaderStyle={calendarHeaderStyle}
          calendarHeaderClassName={calendarHeaderClassName}
        >
          {children}
        </TimelineHeaders>
      );
    }}
  </TimelineHeadersConsumer>
);

TimelineHeadersWrapper.secretKey = 'TimelineHeaders';

export default TimelineHeadersWrapper;
